import {
  Rect,
  type GraphComponent,
  type ILabel,
  type IRenderContext,
} from '@ardoq/yfiles';
import type { JSONLabel } from '@ardoq/api-types';

export type LabelStyleProperties = {
  // Additional properties for extensibility
  [key: string]: unknown;

  text?: string;
  textLines?: string[];
  lines?: string[] | Array<{ text: string }>;

  fontSize?: number | string;
  fontFamily?: string;
  fontWeight?: string | number;
  fontStyle?: string;

  color?: string;
  fill?: string;
  stroke?: string;
  backgroundColor?: string;

  textAlign?: string;
  verticalAlign?: string;
  padding?: number | string;
  margin?: number | string;

  opacity?: number;
  visibility?: string;
  transform?: string;
};

export type LayoutProvider = (context: IRenderContext, label: ILabel) => Rect;
export type StyleProvider = (
  context: IRenderContext,
  label: ILabel
) => LabelStyleProperties | null;
export type MultiLayoutProvider = (
  context: IRenderContext,
  label: ILabel
) => Rect[];
export type MultiStyleProvider = (
  context: IRenderContext,
  label: ILabel
) => LabelStyleProperties[] | LabelStyleProperties | null;

export const createSingleLabelExporter = (
  getLayout: LayoutProvider,
  getStyle: StyleProvider,
  textExtractor: (
    label: ILabel,
    style: LabelStyleProperties | null
  ) => string = createSingleLabelTextExtractor()
) => {
  return (gc: GraphComponent, lb: ILabel): JSONLabel => {
    const rc: IRenderContext = gc.createRenderContext();
    const styleObj = getStyle(rc, lb);
    const rect = getLayout(rc, lb);
    const text = textExtractor(lb, styleObj);

    return {
      text,
      layout: {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
      },
      style: styleObj ?? undefined,
    };
  };
};

export const createMultiLabelExporter = (
  getLayout: MultiLayoutProvider,
  getStyle: MultiStyleProvider,
  textExtractor: (
    index: number,
    style: LabelStyleProperties,
    styles: LabelStyleProperties[]
  ) => string = createMultiLabelTextExtractor()
) => {
  return (gc: GraphComponent, lb: ILabel): JSONLabel[] => {
    const rc: IRenderContext = gc.createRenderContext();
    const rects = getLayout(rc, lb) ?? [];
    const rawStyles = getStyle(rc, lb);

    const styles: LabelStyleProperties[] = Array.isArray(rawStyles)
      ? rawStyles
      : rawStyles
        ? [rawStyles]
        : [];

    if (!Array.isArray(rects) || rects.length !== styles.length) {
      return [];
    }

    return rects.map((r, i) => {
      const s = styles[i];
      const text = textExtractor(i, s, styles);
      return {
        text,
        layout: { x: r.x, y: r.y, width: r.width, height: r.height },
        style: s,
      } satisfies JSONLabel;
    });
  };
};

const extractTextFromStyle = (style: LabelStyleProperties | null): string => {
  if (!style) return '';

  if (typeof style.text === 'string') {
    return style.text;
  }

  const lines = style.lines;
  if (Array.isArray(lines)) {
    if (lines.every(line => typeof line === 'string')) {
      return lines.join('\n');
    }

    const lineTexts = lines
      .map(ln => (typeof ln === 'string' ? ln : ln.text))
      .filter(text => text.length > 0);
    return lineTexts.join('\n');
  }

  const textLines = style.textLines;
  if (
    Array.isArray(textLines) &&
    textLines.every(line => typeof line === 'string')
  ) {
    return textLines.join('\n');
  }

  return '';
};

export const createSingleLabelTextExtractor = () => {
  return (label: ILabel, style: LabelStyleProperties | null): string => {
    const styleText = extractTextFromStyle(style);
    return styleText || label.text;
  };
};

export const createMultiLabelTextExtractor = () => {
  return (_index: number, style: LabelStyleProperties): string => {
    return extractTextFromStyle(style);
  };
};

export const createLabelTextFallbackExtractor = () => {
  return (label: ILabel, _style: LabelStyleProperties | null): string => {
    return label.text;
  };
};
